import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Info, Layers, Pencil, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { cn } from '../../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { useResponsive } from '../../hooks/useResponsive';
import { ENTITY_PLURAL_KEY_REGISTRY } from '@/generated/entity-urls';
import { getTagFilteredListUrl } from '../../utils/entityUrls';
import { tagsApi } from './tagsApi';
import type { Tag } from '@/generated/api/models/tag';
import type { TagSummary } from '@/generated/api/models/tagSummary';

/**
 * Compute a readable foreground color for an arbitrary background hex.
 * Uses the standard YIQ luminance test — dark backgrounds get a light
 * foreground and vice versa.
 */
function readableForeground(hex: string | null | undefined): string {
  if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) {
    return 'var(--foreground)';
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  // Foreground is computed against an arbitrary user-chosen tag color; Tailwind
  // CSS variables can't deliver a YIQ-paired contrast, so we use literal
  // slate-800 / white pair here.
  /* eslint-disable no-restricted-syntax */
  const DARK_FOREGROUND = '#1f2937'; // slate-800
  const LIGHT_FOREGROUND = '#ffffff'; // white
  /* eslint-enable no-restricted-syntax */
  return yiq >= 160 ? DARK_FOREGROUND : LIGHT_FOREGROUND;
}

export interface TagChipProps {
  tag: Pick<TagSummary, 'id' | 'name' | 'color'>;
  size?: 'sm' | 'default';
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  onRemove?: () => void;
  removeAriaLabel?: string;
  /**
   * {module}.{entity} context. When provided (and neither `href` nor `onClick`
   * is set), the chip becomes an "attached" chip: it shows a hover tooltip with
   * the tag's description + usage, and clicking it navigates to that entity
   * type's list page filtered by this tag.
   */
  entityType?: string;
  className?: string;
  /**
   * Optional trailing affordance rendered inside the pill, after the name and
   * before the remove button. Used on touch devices to attach the tag-detail
   * popover trigger (see {@link InteractiveTagChip}).
   */
  trailing?: React.ReactNode;
}

/**
 * Pure visual chip — a colored pill with automatic readable foreground; falls
 * back to a neutral chip when `color` is null.
 *
 * The pill itself is a non-interactive `<span>`; the name (optionally a router
 * link or a button) and the remove button are siblings inside it, so a
 * removable + clickable chip never nests interactive elements (valid HTML).
 */
function BaseTagChip({
  tag,
  size = 'default',
  href,
  onClick,
  onRemove,
  removeAriaLabel,
  className,
  trailing,
}: Omit<TagChipProps, 'entityType'>) {
  const { t } = useTranslation('common');
  const color = tag.color ?? null;
  const style = color
    ? { backgroundColor: color, color: readableForeground(color) }
    : undefined;

  const sizeClasses = size === 'sm' ? 'h-5 text-[11px]' : 'h-6 text-xs';
  const padStart = size === 'sm' ? 'pl-1.5' : 'pl-2';
  const padEnd =
    onRemove || trailing
      ? size === 'sm'
        ? 'pr-0.5'
        : 'pr-1'
      : size === 'sm'
        ? 'pr-1.5'
        : 'pr-2';

  const pill = cn(
    'inline-flex items-center gap-1 rounded-full font-medium leading-none transition-colors',
    color ? '' : 'bg-muted text-foreground border border-border',
    sizeClasses,
    padStart,
    padEnd,
    className,
  );

  const interactive = !!(href || onClick);
  const nameClass = cn(
    'truncate max-w-[160px] rounded-full text-inherit',
    interactive &&
      'cursor-pointer hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  );

  const nameNode = href ? (
    <Link to={href} className={nameClass} onClick={onClick}>
      {tag.name}
    </Link>
  ) : onClick ? (
    <button type="button" className={nameClass} onClick={onClick}>
      {tag.name}
    </button>
  ) : (
    <span className={nameClass}>{tag.name}</span>
  );

  return (
    <span className={pill} style={style}>
      {nameNode}
      {trailing}
      {onRemove && (
        <button
          type="button"
          aria-label={removeAriaLabel ?? t('tags.removeTag', { name: tag.name })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-foreground/10"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

/**
 * Shared body for the tag-detail overlay — identical content whether it is shown
 * in the desktop hover tooltip or the touch popover. Lazily-loaded description +
 * usage, the "view in module" link, and the cross-entity ("view everywhere")
 * link. `onNavigate` lets the touch popover close itself when a link is tapped.
 */
function TagDetailOverlayContent({
  tag,
  detail,
  isLoading,
  href,
  entityLabel,
  onNavigate,
}: {
  tag: Pick<TagSummary, 'id' | 'name'>;
  detail: Tag | undefined;
  isLoading: boolean;
  href: string | null;
  entityLabel: string | null;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation('common');
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium">{tag.name}</p>
        <Link
          to={`/tags/${tag.id}`}
          className="text-muted-foreground hover:text-foreground transition-colors"
          title={t('tags.editTag')}
          aria-label={t('tags.editTag')}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate?.();
          }}
        >
          <Pencil className="h-3 w-3" />
        </Link>
      </div>
      {isLoading && (
        <p className="text-xs text-muted-foreground">{t('app.loading')}</p>
      )}
      {detail?.description && (
        <p className="text-xs text-popover-foreground">{detail.description}</p>
      )}
      <div className="space-y-1 pt-0.5">
        {href && entityLabel && (
          <Link
            to={href}
            onClick={() => onNavigate?.()}
            className="flex items-center gap-1 text-xs text-popover-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            {t('tags.viewInModule', { entity: entityLabel })}
          </Link>
        )}
        {/* Cross-entity tag page — open to every user, scoped to all modules. */}
        {detail && (
          <Link
            to={`/tags/${tag.id}`}
            onClick={() => onNavigate?.()}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Layers className="h-3 w-3 flex-shrink-0" />
            {t('tags.totalCount', { count: detail.total_usage_count })}
          </Link>
        )}
      </div>
    </>
  );
}

/**
 * An "attached" chip with an entity-type context. The chip body always navigates
 * to the entity type's tag-filtered list page on click/tap. The tag's
 * description + usage + cross-entity ("view everywhere") link are surfaced via:
 *
 * - **Desktop** (non-touch): a hover `Tooltip` (relies on the app-level
 *   `TooltipProvider` mounted in App.tsx).
 * - **Touch devices**: hover is unreachable, so the same content is shown in a
 *   `Popover` opened by a small info affordance appended to the chip. The chip's
 *   primary tap target is unchanged.
 */
function InteractiveTagChip({ tag, entityType, ...rest }: TagChipProps) {
  const { t } = useTranslation('common');
  const { isTouchDevice } = useResponsive();
  const [open, setOpen] = useState(false);

  const { data: detail, isLoading } = useQuery({
    queryKey: ['tag-detail', tag.id],
    queryFn: () => tagsApi.get(tag.id),
    enabled: open,
    staleTime: 60_000,
  });

  const href = entityType ? getTagFilteredListUrl(entityType, [tag.id]) : null;
  const pluralKey = entityType ? ENTITY_PLURAL_KEY_REGISTRY[entityType] : null;
  const entityLabel = pluralKey ? t(pluralKey) : null;

  const overlay = (
    <TagDetailOverlayContent
      tag={tag}
      detail={detail}
      isLoading={isLoading}
      href={href}
      entityLabel={entityLabel}
      onNavigate={isTouchDevice ? () => setOpen(false) : undefined}
    />
  );

  // Touch devices can't hover — attach an info affordance that opens a popover
  // with the same content, leaving the chip's tap-to-filter target intact.
  if (isTouchDevice) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <BaseTagChip
          tag={tag}
          {...rest}
          href={href ?? undefined}
          trailing={
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={t('tags.tagDetails', { name: tag.name })}
                // Sibling of the chip's link — only stop bubbling so a tap on
                // the affordance can't trigger an enclosing row/card click.
                // Must NOT preventDefault: Radix's PopoverTrigger skips its own
                // toggle handler when the composed event has its default
                // prevented (checkForDefaultPrevented).
                onClick={(e) => e.stopPropagation()}
                // The chip's name link sits immediately to the LEFT of this
                // button, so the enlarged touch area must grow only rightward
                // and vertically (`left-0`) — a symmetric `-inset` would cover
                // the short link and hijack the chip's primary tap target.
                className="relative inline-flex h-4 w-4 items-center justify-center rounded-full text-inherit hover:bg-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring after:absolute after:-inset-y-2.5 after:left-0 after:-right-3 after:content-['']"
              >
                <Info className="h-3 w-3" />
              </button>
            </PopoverTrigger>
          }
        />
        <PopoverContent
          side="bottom"
          align="start"
          collisionPadding={16}
          className="w-[260px] max-w-[calc(100vw-2rem)] space-y-1.5 p-3"
        >
          {overlay}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Tooltip open={open} onOpenChange={setOpen} delayDuration={300}>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <BaseTagChip tag={tag} {...rest} href={href ?? undefined} />
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[260px] space-y-1.5">
        {overlay}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Single source of truth for tag visual styling.
 *
 * Plain by default. When given an `entityType` (and no explicit `href`/
 * `onClick`), the chip becomes interactive — hover tooltip + click-through to
 * the tag-filtered list page.
 */
export function TagChip(props: TagChipProps) {
  const { entityType, href, onClick } = props;
  if (entityType && !href && !onClick) {
    return <InteractiveTagChip {...props} />;
  }
  return <BaseTagChip {...props} />;
}

export interface TagChipStackProps {
  tags: Array<Pick<TagSummary, 'id' | 'name' | 'color'>>;
  size?: 'sm' | 'default';
  max?: number;
  className?: string;
  /** Optional click handler invoked when a chip is clicked. */
  onTagClick?: (tag: TagSummary) => void;
  /**
   * {module}.{entity} context — makes each chip interactive (hover tooltip +
   * click-through to the tag-filtered list). Ignored when `onTagClick` is set.
   */
  entityType?: string;
}

/**
 * Renders a horizontal stack of TagChips with `+N` overflow. Used in list
 * page Tags columns and on kanban cards.
 */
export function TagChipStack({
  tags,
  size = 'sm',
  max = 3,
  className,
  onTagClick,
  entityType,
}: TagChipStackProps) {
  if (!tags || tags.length === 0) {
    return null;
  }
  const visible = tags.slice(0, max);
  const overflow = tags.length - visible.length;
  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {visible.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          size={size}
          entityType={onTagClick ? undefined : entityType}
          onClick={onTagClick ? () => onTagClick(tag as TagSummary) : undefined}
        />
      ))}
      {overflow > 0 && (
        <span className="inline-flex h-5 items-center rounded-full bg-muted px-1.5 text-[11px] text-muted-foreground">
          +{overflow}
        </span>
      )}
    </div>
  );
}
